import {IterativeSystem} from "tick-knock";
import {CapitalView} from "@game/coregame/view/CapitalView";
import {IVibrationManager, TypesCore, TypesSocial} from "@dp-instant/interfaces";
import {Action, di, EventDispatcher, lazyGet} from "@dp-instant/core";
import {Building} from "@game/coregame/ecs/entities/Building";
import {math} from "@game/coregame/helpers";
import {GameEvents} from "@game/coregame/GameEvents";
import {ActiveBuildingsQuery} from "@game/coregame/ecs/queries/ActiveBuildingsQuery";
import {Point, Ticker} from "pixi.js-legacy";

type PointLike = {x: number; y: number};

export const distanceBetweenPoints = (p1: PointLike, p2: PointLike) =>
    math.dist([p1.x, p1.y], [p2.x, p2.y]);

export class InputSystem extends IterativeSystem {
    private _dispatcher: EventDispatcher;

    protected get dispatcher(): EventDispatcher {
        return (this._dispatcher ||= di.get(TypesCore.dispatcher));
    }

    private static _multiselectDistanceMax = Number.MAX_VALUE;
    private static _timeForMultiselectAdding = 0.25;

    private _buildingHitThisFrame?: Building;
    private _buildingHitLastFrame?: Building;
    private _lastSelectedTargetBuilding?: Building;
    private _selected: Set<Building> = new Set<Building>();
    private _isTouchStartedOnBuilding = false;
    private _wasBuildingInteracted = false;
    private _multiselectTimer = 0;

    constructor() {
        super(ActiveBuildingsQuery);
    }

    public onCancel(event: PointerEvent): void {
        this.dispatcher.emit(GameEvents.AIM_REMOVE);
        this.deselectLast();
    }
    public onStart(event: PointerEvent): void {
        // this.dispatcher.emit(GameEvents.AIM_REMOVE);
        // this.deselectLast();

        this._buildingHitThisFrame = this.getClosest(new Point(event.clientX, event.clientY));
        if (this._buildingHitThisFrame?.isFirstPlayer) {
            this._isTouchStartedOnBuilding = true;
            if (!this._buildingHitThisFrame.selected) {
                this.selectAsSource(this._buildingHitThisFrame);
            }
        }
    }

    public onDrag(event: PointerEvent): void {
        if (this._selected.size == 0 || !this._isTouchStartedOnBuilding) return;
        const touchPos = new Point(event.clientX, event.clientY);
        const hitted = this.getClosest(touchPos);

        if (hitted != this._buildingHitLastFrame) {
            this._wasBuildingInteracted = false;
        }
        const Time_deltaTime = Ticker.shared.deltaMS / 1000;

        if (hitted) {
            const hitView = hitted.get(CapitalView);
            if (hitView) {
                const hittedPosition = hitView.toGlobal(new Point(0, 0));
                if (!this._selected.has(hitted)) {
                    if (hitted.isFirstPlayer && !this._wasBuildingInteracted) {
                        const distance = distanceBetweenPoints(hittedPosition, touchPos);
                        if (distance < InputSystem._multiselectDistanceMax) {
                            if (this._buildingHitLastFrame == hitted) {
                                this._multiselectTimer += Time_deltaTime; // Time.deltaTime;
                                if (
                                    this._multiselectTimer >= InputSystem._timeForMultiselectAdding
                                ) {
                                    this.selectAsSource(hitted);
                                    this._multiselectTimer = 0;
                                    this._wasBuildingInteracted = true;
                                }
                            } else {
                                this._multiselectTimer = 0;
                            }
                        }
                    }
                } else {
                    if (
                        hitted.isFirstPlayer &&
                        this._selected.size > 1 &&
                        !this._wasBuildingInteracted
                    ) {
                        const distance = distanceBetweenPoints(hittedPosition, touchPos);
                        if (distance < InputSystem._multiselectDistanceMax) {
                            if (this._buildingHitLastFrame == hitted) {
                                this._multiselectTimer += Time_deltaTime; // Time.deltaTime;
                                if (
                                    this._multiselectTimer >= InputSystem._timeForMultiselectAdding
                                ) {
                                    this.removeSource(hitted);
                                    this._multiselectTimer = 0;
                                    this._wasBuildingInteracted = true;
                                }
                            } else {
                                this._multiselectTimer = 0;
                            }
                        }
                    }

                    if (this._lastSelectedTargetBuilding) {
                        this._lastSelectedTargetBuilding?.deselect();
                        delete this._lastSelectedTargetBuilding;
                    }
                }
            }

            if (hitted != this._lastSelectedTargetBuilding) {
                this._lastSelectedTargetBuilding?.deselect();
                this._lastSelectedTargetBuilding = hitted;
                this._lastSelectedTargetBuilding.selectAsTarget();
            }
        } else {
            this._lastSelectedTargetBuilding?.deselect();
            delete this._lastSelectedTargetBuilding;
            this.dispatcher.emit(GameEvents.AIM_SET, null);
        }

        this._buildingHitLastFrame = this.getClosest(touchPos);
        this.dispatcher.emit(GameEvents.AIM_UPDATE, event);
    }

    public onEnd(event: PointerEvent): void {
        if (this._lastSelectedTargetBuilding) {
            this._selected.forEach(item => {
                if (item !== this._lastSelectedTargetBuilding && item.isFirstPlayer) {
                    item.sendTo(this._lastSelectedTargetBuilding!);
                }
            });
            const lastSelectedTargetBuildingView =
                this._lastSelectedTargetBuilding.get(CapitalView);
            if (lastSelectedTargetBuildingView) {
                this.dispatcher.emit(
                    GameEvents.AIM_SET,
                    lastSelectedTargetBuildingView.toGlobal(new Point(0, 0))
                );
            }
            this._lastSelectedTargetBuilding.deselect();
            delete this._lastSelectedTargetBuilding;
            // TODO: dispatch sent
        }
        this.dispatcher.emit(GameEvents.AIM_REMOVE);

        this.deselectLast();
    }

    protected getClosest(globalPoint: Point): Building | undefined {
        let closestDistance = Number.MAX_VALUE;
        let closestEntity: Building | undefined;

        this.entities.forEach(entity => {
            const view = entity.get(CapitalView);
            if (view) {
                const vec = view.toLocal(globalPoint);
                const distance = Math.sqrt(vec.x * vec.x + vec.y * vec.y);
                if (distance < closestDistance) {
                    closestEntity = entity as Building;
                    closestDistance = distance;
                }
            }
        });
        return closestEntity;
    }

    protected selectAsSource(building: Building) {
        this._selected.add(building);
        building.selectAsSource();
        this.dispatcher.emit(GameEvents.AIM_CREATE, building);
        // _vibrationManager.VibratePeek();
        this.vibratePeek();
    }

    protected removeSource(building: Building) {
        if (this._selected.has(building)) {
            this._selected.delete(building);
            this.dispatcher.emit(GameEvents.AIM_HIDE, building);
            // _vibrationManager.VibratePeek();
            this.vibratePeek();
        }
    }

    protected vibratePeek() {
        const vibrationManager = lazyGet<IVibrationManager>(TypesSocial.vibrationManager);
        vibrationManager?.vibrate(); // vibrate
    }
    protected deselectLast() {
        this._selected.forEach(item => item.deselect());
        this._selected.clear();
    }

    protected updateEntity() {
        // nothing
    }
}
